import React, {useLayoutEffect, useRef, useState} from 'react';
import {Box} from "@mui/material";
import {Typography} from "@mui/joy";
import {ChevronRightRounded} from "@mui-symbols-material/w300";
import {InstalledBasePathNode} from "../../utils/installedBasePath";
import {getVisibleStartIndex} from "../../utils/collapseLeadingCrumbs";
import InstalledBasePathTooltip from "../InstalledBasePathTooltip";

interface Props {
    /** Root → leaf, already without the node itself (see getPathNodesAbove). */
    pathNodes: InstalledBasePathNode[];
}

const textSx = {color: '#333', fontWeight: 'lg'};

// Padding instead of margin/gap so offsetWidth captures the separator's full footprint.
const Separator = React.forwardRef<HTMLSpanElement>((_props, ref) => (
    <Box component="span" ref={ref} sx={{display: 'flex', alignItems: 'center', px: '4px', flexShrink: 0}}>
        <ChevronRightRounded sx={{fontSize: 16, color: '#999'}}/>
    </Box>
));

/**
 * Installed base path on a single line, "A > B > C" like the hero customer card.
 * When it doesn't fit, whole leading elements are replaced by a "… >" prefix (the
 * last element is always kept and ellipsized if needed). Widths come from a hidden
 * measurement row, a ResizeObserver keeps them in sync with the container — the same
 * approach as InstalledBaseNodePathBreadcrumb. The full path is shown on hover.
 */
const InstalledBasePathLine = ({pathNodes}: Props) => {
    const crumbs = pathNodes.filter((node) => !!node.title);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const crumbRefs = useRef<(HTMLElement | null)[]>([]);
    const ellipsisRef = useRef<HTMLElement | null>(null);
    const separatorRef = useRef<HTMLElement | null>(null);
    const [visibleStartIndex, setVisibleStartIndex] = useState(0);

    const crumbsKey = crumbs.map((crumb) => `${crumb.id}:${crumb.title}`).join('|');

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const recompute = () => {
            const widths = crumbs.map((crumb, index) => crumbRefs.current[index]?.offsetWidth ?? 0);
            setVisibleStartIndex(getVisibleStartIndex(
                widths,
                separatorRef.current?.offsetWidth ?? 0,
                ellipsisRef.current?.offsetWidth ?? 0,
                container.clientWidth,
            ));
        };

        recompute();
        const observer = new ResizeObserver(recompute);
        observer.observe(container);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [crumbsKey]);

    if (!crumbs.length) return null;

    const truncated = visibleStartIndex > 0;
    const visibleCrumbs = crumbs.slice(visibleStartIndex);

    return (
        <InstalledBasePathTooltip pathNodes={crumbs}>
            <Box
                ref={containerRef}
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    minWidth: 0,
                }}
            >
                {truncated && (
                    <>
                        <Typography level={'body-sm'} sx={{...textSx, flexShrink: 0}}>…</Typography>
                        <Separator/>
                    </>
                )}
                {visibleCrumbs.map((crumb, index) => {
                    const isLast = index === visibleCrumbs.length - 1;
                    return (
                        <React.Fragment key={crumb.id ?? index}>
                            {index > 0 && <Separator/>}
                            <Typography
                                level={'body-sm'}
                                sx={isLast
                                    // minWidth/ellipsis so a single overlong crumb still stays on one line.
                                    ? {...textSx, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis'}
                                    : {...textSx, flexShrink: 0}}
                            >
                                {crumb.title}
                            </Typography>
                        </React.Fragment>
                    );
                })}
                {/* Hidden measurement row: all crumbs at natural width. */}
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        visibility: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                    }}
                >
                    <Typography level={'body-sm'} sx={textSx} ref={(el) => { ellipsisRef.current = el; }}>…</Typography>
                    <Separator ref={(el) => { separatorRef.current = el; }}/>
                    {crumbs.map((crumb, index) => (
                        <Typography
                            key={crumb.id ?? index}
                            level={'body-sm'}
                            sx={textSx}
                            ref={(el) => { crumbRefs.current[index] = el; }}
                        >
                            {crumb.title}
                        </Typography>
                    ))}
                </Box>
            </Box>
        </InstalledBasePathTooltip>
    );
};

export default InstalledBasePathLine;
